import React, { useEffect, useMemo, useState } from 'react';
import { CommonInput, Modal } from '@/components';
import CreateJKCTaskApi from '@/fetch/business/jkc/createTask';
import { HttpStatusCode } from '@/fetch/core/constant';
import { showErrorToast, showSuccessToast } from '@/utils/toast';
import { debounce } from '@/utils';
import './index.scss';

interface ContactLookupResult {
  contactUserName: string;
  contactUserErp: string;
  contactUserPhone: string;
  contactUserId: number | string;
  contactUserBelongCode: number | string;
}

interface ContactFormErrors {
  phone: string;
  name: string;
}

const MAINLAND_PHONE_REGEX = /^1[3-9]\d{9}$/;

function AddContactModal({
  visible,
  onClose,
  onSuccess
}: {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<ContactFormErrors>({
    phone: '',
    name: ''
  });
  const [lookupResult, setLookupResult] = useState<ContactLookupResult | null>(
    null
  );
  const [isLookingUp, setIsLookingUp] = useState(false);

  useEffect(() => {
    if (!visible) {
      setPhone('');
      setName('');
      setErrors({ phone: '', name: '' });
      setLookupResult(null);
      setIsLookingUp(false);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setName('');
    setLookupResult(null);
    setErrors((prev) => ({
      phone:
        phone.length === 11 && !MAINLAND_PHONE_REGEX.test(phone)
          ? '请输入正确的手机号'
          : '',
      name: ''
    }));

    if (!MAINLAND_PHONE_REGEX.test(phone)) {
      setIsLookingUp(false);
      return;
    }

    let cancelled = false;
    const queryPhone = phone;

    const fetchUserByPhone = async () => {
      setIsLookingUp(true);
      try {
        const response = await CreateJKCTaskApi.getUserByPhone({
          phone: queryPhone
        });
        const isContactInfoMissing = !response.data?.contactUserName;
        if (cancelled) {
          return;
        }

        if (response.code === HttpStatusCode.Success && !isContactInfoMissing) {
          setLookupResult(response.data);
          setName(response.data.contactUserName);
          setErrors((prev) => ({ ...prev, name: '' }));
        } else {
          const errorMessage = isContactInfoMissing
            ? '未查询到联系人信息'
            : response.message || '查询联系人失败';
          setLookupResult(null);
          setName('');
          setErrors((prev) => ({
            ...prev,
            name: errorMessage
          }));
          showErrorToast(errorMessage);
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        setLookupResult(null);
        setName('');
        setErrors((prev) => ({
          ...prev,
          name: '未查询到联系人信息'
        }));
        showErrorToast('查询联系人失败');
      } finally {
        if (!cancelled) {
          setIsLookingUp(false);
        }
      }
    };

    fetchUserByPhone();

    return () => {
      cancelled = true;
    };
  }, [phone, visible]);

  const canSubmit = useMemo(() => {
    return (
      MAINLAND_PHONE_REGEX.test(phone) &&
      !!lookupResult &&
      !!name &&
      !isLookingUp
    );
  }, [isLookingUp, lookupResult, name, phone]);

  const handlePhoneChange = (value: string) => {
    setPhone(value);
  };

  const submit = async () => {
    if (!canSubmit || !lookupResult) {
      setErrors({
        phone: MAINLAND_PHONE_REGEX.test(phone) ? '' : '请输入正确的手机号',
        name: name ? '' : '请先查询联系人姓名'
      });
      return;
    }

    try {
      const response = await CreateJKCTaskApi.addContact({
        contactUserName: lookupResult.contactUserName,
        contactUserErp: lookupResult.contactUserErp || '',
        contactUserPhone: lookupResult.contactUserPhone || phone,
        contactUserId: lookupResult.contactUserId
      });

      if (response.code === HttpStatusCode.Success) {
        showSuccessToast('添加联系人成功');
        onSuccess();
      } else {
        showErrorToast(response.message || '添加联系人失败');
      }
    } catch (error) {
      showErrorToast('添加联系人失败');
    }
  };

  const debouncedSubmit = debounce(submit, 500, true);

  return (
    <Modal
      visible={visible}
      title="添加其他联系人"
      className="jkc-add-contact-modal"
      content={
        <div className="jkc-add-contact-modal__content">
          <CommonInput
            label="手机号"
            value={phone}
            placeholder="请输入手机号"
            maxLength={11}
            errorMessage={errors.phone}
            type="number"
            onChange={handlePhoneChange}
          />
          <div className="common-input-item">
            <div className="common-input-label required">姓名</div>
            <div className="common-input-wrapper">
              <span
                className={`jkc-add-contact-modal__name-display ${!name ? 'is-placeholder' : ''}`}
              >
                {name || (isLookingUp ? '查询中...' : '将自动回填')}
              </span>
              {errors.name && (
                <div className="common-input-error">{errors.name}</div>
              )}
            </div>
          </div>
        </div>
      }
      footer={[
        {
          type: 'default',
          btnText: '取消',
          autoClose: true
        },
        {
          type: 'primary',
          btnText: '确定',
          onClick: debouncedSubmit,
          disabled: !canSubmit
        }
      ]}
      closeModal={onClose}
    />
  );
}

export default AddContactModal;
